import { AemConfiguration, MainWebShopConfiguration } from './AemConfigurationTypes';
import { CountryTranslation } from './checkout';
import { StockLocation } from './enums';

export enum DeliveryOptionsEnum {
  COLLECT_STORE = 'COLLECT_STORE',
  COLLECT_HQ = 'COLLECT_HQ',
  HOME_DELIVERY = 'HOME_DELIVERY',
  NEXT_DAY = 'NEXT_DAY',
  NAMED_DAY = 'NAMED_DAY',
  PICKUP_POINT = 'PICKUP_POINT',
}

export type DeliveryPromiseResponseObject = {
  cutoff?: string;
  deliveryFrom: number;
  deliveryTo: number;
  deliveryUnit: string;
  earliestDeliveryDate: boolean;
  dispatchDate: Date;
  deliveryDate: Date;
  latestDispatchDay: Date;
  ultimateDispatchDay: Date;
  messages: Record<DeliveryOptionsEnum, string>;
  preorder: boolean;
  nextDayDeliverable: boolean;
};

export type DeliveryPromiseCCPromise = {
  market: string;
  shopId: number;
  inWeb: number;
  active: boolean;
  delivery: string;
  physicalRequestableStockInStore: number;
  earliestDelayedDate?: string;
  latestDelayedDate?: string;
};

export type DeliveryPromiseHDPromise = {
  hasStock: boolean;
  stockLocation: StockLocation;
  messages: Record<DeliveryOptionsEnum, string>;
  deliveryDateFrom: string;
  deliveryDateTo: string;
  nextDayDeliverable: boolean;
  cutoff?: string;
  deliveryUnit?: string;
};

export type DeliveryPromiseExtendedResponseObject = {
  productId: number;
  availableForCc?: boolean;
  availableForHd?: boolean;
  skuId?: number;
  amountOfShopAvailableIn?: number;
  amountOfShopDelayedAvailableIn?: number;
  earliestDelayedDate?: string;
  latestDelayedDate: string;
  hdDeliveryPromise?: DeliveryPromiseHDPromise;
  clickAndCollectPromise?: DeliveryPromiseCCPromise;
  sseProduct?: boolean;
  loading?: boolean;
};

export type DeliveryPromise = {
  deliveryFrom?: number;
  deliveryTo?: number;
  deliveryUnit?: string;
  skuId: number;
  shopId?: null;
  market?: null;
  inWeb: number;
  productId: number;
  active: boolean;
  clickAndCollect?: boolean;
  quantity?: number;
  soldOut?: boolean;
  deliveryPromises: Array<DeliveryPromiseItem>;
  stockLocation?: StockLocation;
  deliveryDateTo?: string | number;
  deliveryDateFrom?: string | number;
};

export type DeliveryPromiseItem = {
  name: string;
  deliveredTomorrow: boolean;
  hasDeliveryMessage: boolean;
  deliveryMessage: string;
  delivery?: boolean;
  deliveryFrom?: string;
  deliveryTo?: string;
  deliveryUnit?: string;
  cutoff?: string;
};

export type DeliveryPromiseRequestObject = {
  colourId: number;
  productId: number;
  skuId: number;
};

export type DeliveryPromiseGetForExtensiveTileProductObject = {
  productId: string;
  skuId: string;
  colourId: number;
  productGroupId?: string;
};

export type DeliveryPromiseSetForExtensiveTileProductObject = {
  productId: string;
  deliveryPromise: DeliveryPromise;
  colourId: number;
  productGroupId?: string;
};

export type DeliveryPromiseStoreProduct = {
  productId: number;
  skuId: number;
  deliveryMessage: string;
  stockLocation: StockLocation;
};

export type DeliveryPromiseBasketResponseObject = DeliveryPromiseResponseObject & {
  nextDayResult?: {
    daysUntilDelivery: number;
    cutoffAsTime: string;
    cutoffAsUTC: string;
    beforeCutoff: boolean;
  };
};

export const deliveryDay = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
} as const;

export type NextDayDeliveryInfo = {
  cutOffTime: string;
  cutOffUTC: string;
  dispatch: boolean;
  delivery: boolean;
};

export type NextDayDeliveryInfoDay = NextDayDeliveryInfo & {
  day: keyof typeof deliveryDay;
};

export type NextDayDeliveryInfoException = NextDayDeliveryInfo & {
  date: string;
};

export type NextDayDeliveryInfoRequestObject = {
  market: MainWebShopConfiguration['market'];
  mainWebShopId: MainWebShopConfiguration['mainWebshopId'];
  fictiveWebShopId: AemConfiguration['fictiveWebShop'];
  days: Array<NextDayDeliveryInfoDay>;
  exceptions: Array<NextDayDeliveryInfoException>;
};

type DeliveryCharge = {
  minValue: number;
  maxValue: number;
  deliveryCost: number;
};

export type DeliveryChargesOption = {
  name: DeliveryOptionsEnum;
  charges: Array<DeliveryCharge>;
};

export type DeliveryChargesRequestObject = {
  market: MainWebShopConfiguration['market'];
  mainWebShopId: MainWebShopConfiguration['mainWebshopId'];
  webShopId: AemConfiguration['fictiveWebShop'];
  zoneId: number;
  countryId: number;
  countryIso: string;
  countryCode: string;
  deliveryMethodId: number;
  serviceLevelId: number;
  vatFree: boolean;
  active: boolean | null;
  deliveryOptions: Array<DeliveryChargesOption>;
  countryTranslations: Record<string, CountryTranslation>;
};
